// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Shared UI for the inventory and fixed-asset rollforward close steps: a per-currency waterfall
 * chart plus a reviewable journal-entries table. Section auto-advance and the disabled-Continue
 * flourish come from usePerCurrencyReview. The caller supplies only the currency-specific chart.
 */
import {
  CloseRunCurrencyBucket,
  JournalEntryWithReviewState,
} from '@/accounting/api/types';
import { JournalEntriesTable } from '@/accounting/components/journal-entries-table';
import { ReviewStatusChip } from '@/accounting/components/review-status-chip';
import { ReviewStepNavigation } from '@/accounting/components/review-step-navigation';
import { usePerCurrencyReview } from '@/accounting/hooks/use-per-currency-review';
import { toPerCurrencySectionBuckets } from '@/accounting/utilities/to-per-currency-section-buckets';
import { PerCurrencySections } from '@/billing/components/currency-section-accordion';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { AssetWaterfallControls } from '@/report/components/asset-waterfall-controls';
import { RollforwardWaterfallDisplayMetricType } from '@/report/types';
import { Box, Card, CardContent, CardHeader } from '@mui/material';
import { ReactNode, useState } from 'react';

interface Props {
  entries: JournalEntryWithReviewState[];
  buckets: CloseRunCurrencyBucket[];
  onEntriesChange: (entries: JournalEntryWithReviewState[]) => void;
  onNext: () => void;
  onBack: () => void;
  // Namespaces the per-section DOM ids so the two rollforward steps never collide.
  idNamespace: string;
  chartTitle: string;
  chartSubheader: string;
  journalSubheader: string;
  emptyMessage: string;
  continueLabel: string;
  // Renders the currency-specific waterfall chart (inventory vs fixed asset) for a bucket.
  renderChart: (
    bucket: CloseRunCurrencyBucket,
    metricType: RollforwardWaterfallDisplayMetricType
  ) => ReactNode;
}

export function RollforwardReviewStep(props: Props) {
  // Keyed per currency: the toggle renders inside each section with that section's currency
  // symbol and noun, so a single shared value would let one section's toggle silently change
  // another's chart and its own displayed state.
  const [metricTypeByCurrencyCode, setMetricTypeByCurrencyCode] = useState<
    Record<string, RollforwardWaterfallDisplayMetricType>
  >({});
  const metricTypeFor = (currencyCode: IsoCurrencyCode) =>
    metricTypeByCurrencyCode[currencyCode] ?? 'dollars';
  const setMetricTypeFor =
    (currencyCode: IsoCurrencyCode) =>
    (next: RollforwardWaterfallDisplayMetricType) => {
      setMetricTypeByCurrencyCode((prev) => ({
        ...prev,
        [currencyCode]: next,
      }));
    };
  const review = usePerCurrencyReview({
    entries: props.entries,
    buckets: props.buckets,
    onEntriesChange: props.onEntriesChange,
    idNamespace: props.idNamespace,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PerCurrencySections
        buckets={toPerCurrencySectionBuckets(props.buckets)}
        emptyMessage={props.emptyMessage}
        prominentHeader
        sectionIdPrefix={review.sectionIdPrefix}
        expandedCurrencyCodes={review.expandedCurrencyCodes}
        onToggleSection={review.onToggleSection}
        renderHeaderChip={(bucket) => (
          <ReviewStatusChip
            isReviewed={review.sectionPendingCount(bucket.currency) === 0}
          />
        )}
        renderBucket={(bucket) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card variant="outlined">
              <CardHeader
                title={props.chartTitle}
                subheader={props.chartSubheader}
                action={
                  <AssetWaterfallControls
                    metricType={metricTypeFor(bucket.currency)}
                    onMetricTypeChange={setMetricTypeFor(bucket.currency)}
                    currencyCode={bucket.currency}
                  />
                }
                sx={{ pb: 1 }}
              />
              <CardContent>
                {props.renderChart(bucket, metricTypeFor(bucket.currency))}
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardHeader
                title="Associated journal entries"
                subheader={props.journalSubheader}
                sx={{ pt: 3, pb: 1 }}
              />
              <CardContent>
                <JournalEntriesTable
                  entries={props.entries.filter(
                    (entry) => entry.currency === bucket.currency
                  )}
                  onMarkReviewed={review.handleMarkReviewed}
                  currencyCode={bucket.currency}
                  flashEntryId={review.flashEntryId}
                  onFlashComplete={review.onFlashComplete}
                />
              </CardContent>
            </Card>
          </Box>
        )}
      />

      <ReviewStepNavigation
        onBack={props.onBack}
        onNext={props.onNext}
        continueLabel={props.continueLabel}
        allReviewed={review.allReviewed}
        onContinueBlocked={review.onContinueBlocked}
      />
    </Box>
  );
}
